import React, { useMemo, useState } from 'react'
import { ClientiStatsState, OrdersDomainState } from './localstate'
import { useQsDataTable as useDataTable } from 'react-use-datatable/react'
import CircularProgress from '@material-ui/core/CircularProgress'
import { Button } from 'reactstrap'
import { useRunRj } from 'react-rocketjump'
import { AgentiState } from '../AdminPreventiviApp/localstate'
import moment from 'moment'
import Column from '../../components/Column'
import { useHistory } from 'react-router-dom'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import Select from 'react-select'
import * as XLSX from 'xlsx'
import {
  DifferenceCell,
  EuroCell,
  HighlightCell,
  StatsMonthRangeFilter,
  StatsTypeToggle,
  TotalBadgeCell,
  formatStatsEuro,
  formatStatsPercentGap,
  formatStatsPercentValue,
  getDifferenceColor,
} from './Stats/components'

const toNumber = (value) => {
  if (value === null || value === undefined || value === '') {
    return 0
  }
  return Number(String(value).replace(',', '.'))
}

const getDateRangeSuffix = (filters) =>
  `${filters.data_inizio || 'inizio'}_${filters.data_fine || 'fine'}`

const writeWorkbook = (filename, sheets) => {
  const workbook = XLSX.utils.book_new()
  sheets.forEach(({ name, rows, widths }) => {
    const worksheet = XLSX.utils.json_to_sheet(rows)
    if (widths) {
      worksheet['!cols'] = widths.map((wch) => ({ wch }))
    }
    XLSX.utils.book_append_sheet(workbook, worksheet, name)
  })
  XLSX.writeFile(workbook, filename)
}

const getClienteAgentiNames = (cliente, agenti = []) =>
  (cliente.agenti || [])
    .map((codiceAgente) => {
      const agente = agenti.find((item) => item.codice === codiceAgente)
      if (!agente) return ''
      return `${agente.user.first_name} ${agente.user.last_name}`
    })
    .filter(Boolean)
    .join(', ')

const buildClientiExportRows = (clienti = [], agenti = []) =>
  clienti.map((cliente) => ({
    Nome: cliente.nome,
    Codice: cliente.codice,
    'Tipo cliente': cliente.tipo_cliente,
    Agente: getClienteAgentiNames(cliente, agenti),
    'N° commesse': cliente.numero_ordini || 0,
    'N° serramenti': cliente.numero_serramenti || 0,
    Listino: toNumber(cliente.listino_serramenti),
    'Sconto %': toNumber(cliente.sconto_serramenti),
    'Media commesse': toNumber(cliente.media_ordini),
    'Media € serramento': toNumber(cliente.media_serramenti),
    Netto: toNumber(cliente.netto_serramenti),
    Budget: toNumber(cliente.budget_cliente),
    'Differenza %': toNumber(cliente.differenza_netto_budget) - 100,
    'Differenza al budget': toNumber(cliente.differenza_netto_budget_euro),
  }))

const buildTotalsExportRow = (totals) => ({
  Nome: 'Totale',
  Codice: '',
  'Tipo cliente': '',
  Agente: '',
  'N° commesse': totals?.totale_ordini || 0,
  'N° serramenti': totals?.totale_serramenti || 0,
  Listino: toNumber(totals?.totale_listino),
  'Sconto %': toNumber(totals?.totale_sconto),
  'Media commesse': toNumber(totals?.totale_media_ordini),
  'Media € serramento': toNumber(totals?.totale_media_serramenti),
  Netto: toNumber(totals?.totale_netto),
  Budget: toNumber(totals?.totale_budget),
  'Differenza %': toNumber(totals?.totale_differenza_netto_budget) - 100,
  'Differenza al budget': toNumber(totals?.totale_differenza_netto_budget_euro),
})

export default function StatsClienti() {
  const [
    // Data State
    { clienti, loading, totals },
    // Table state
    { ...filters },
    {
      // DT Actions
      onFilterChangeDebounced,
      onFilterChange,
      onFiltersChange,
    },
  ] = useDataTable(ClientiStatsState, () => ({
    search: '',
    agenti: [],
    ordering: 'nome',
    page_size: 10000,
    tipo_cliente: '',
    raggruppa_clienti_budget: '1',
    data_inizio: moment()
      .subtract(1, 'month')
      .startOf('month')
      .format('YYYY-MM-DD'),
    data_fine: moment()
      .subtract(1, 'month')
      .endOf('month')
      .format('YYYY-MM-DD'),
  }))

  const [{ data: agenti }] = useRunRj(AgentiState)

  const agentiOptions = useMemo(() => {
    if (!agenti) return []
    const agi = agenti.map((agente) => ({
      value: agente.id,
      label: agente.user.first_name + ' ' + agente.user.last_name,
    }))
    // return sorted agenti
    return agi.sort((a, b) => {
      if (a.label < b.label) return -1
      if (a.label > b.label) return 1
      return 0
    })
  }, [agenti])

  const [{ data: ordersDomain }] = useRunRj(OrdersDomainState)

  const columnProps = {
    value: filters.ordering,
    onChange: (value) => onFilterChange(value, 'ordering'),
  }

  const history = useHistory()

  const [typeStat, setTypeStat] = useState('table')

  const orderedClienti = useMemo(() => {
    if (!clienti) return []
    return [...clienti].sort((a, b) => {
      if (a.differenza_netto_budget === null) return 1
      if (b.differenza_netto_budget === null) return -1
      return b.differenza_netto_budget - a.differenza_netto_budget
    })
  }, [clienti])

  const chartClienti = useMemo(
    () =>
      orderedClienti.map((cliente) => ({
        ...cliente,
        differenza_netto_budget_gap:
          toNumber(cliente.differenza_netto_budget) - 100,
      })),
    [orderedClienti]
  )

  const selectedAgenti = useMemo(() => {
    if (!filters.agenti || !agenti) {
      return []
    }
    if (typeof filters.agenti === 'string') {
      return filters.agenti.split(',').map((id) => ({
        value: id,
        label:
          agenti.find((agente) => agente.id === Number(id))?.user.first_name +
          ' ' +
          agenti.find((agente) => agente.id === Number(id))?.user.last_name,
      }))
    } else {
      return filters.agenti.map((id) => ({
        value: id,
        label:
          agenti.find((agente) => agente.id === Number(id))?.user.first_name +
          ' ' +
          agenti.find((agente) => agente.id === Number(id))?.user.last_name,
      }))
    }
  }, [filters.agenti, agenti])

  const agentiForQs = useMemo(() => {
    if (!filters.agenti || !agenti) {
      return ''
    }
    if (typeof filters.agenti === 'string') {
      return '&agenti=' + filters.agenti
    } else {
      return filters.agenti.map((id) => '&agenti=' + id).join('')
    }
  }, [filters.agenti, agenti])

  const handleExportExcel = () => {
    const suffix = getDateRangeSuffix(filters)
    const sourceRows =
      typeStat === 'chart'
        ? buildClientiExportRows(orderedClienti, agenti)
        : buildClientiExportRows(clienti, agenti)
    const rows = [buildTotalsExportRow(totals), ...sourceRows]

    if (typeStat === 'chart') {
      writeWorkbook(`statistiche_clienti_grafico_${suffix}.xlsx`, [
        {
          name: 'Avanzamento',
          rows: rows.map((row) => ({
            Nome: row.Nome,
            Codice: row.Codice,
            Netto: row.Netto,
            Budget: row.Budget,
            'Differenza %': row['Differenza %'],
            'Differenza al budget': row['Differenza al budget'],
            'N° commesse': row['N° commesse'],
          })),
          widths: [30, 14, 16, 16, 18, 18, 14],
        },
      ])
      return
    }

    writeWorkbook(`statistiche_clienti_tabella_${suffix}.xlsx`, [
      {
        name: 'Statistiche',
        rows,
        widths: [30, 14, 18, 28, 14, 14, 16, 12, 16, 18, 16, 16, 18, 18],
      },
    ])
  }

  return (
    <div className="container-fluid">
      {clienti === null && loading && (
        <div className="text-center mt-4">
          <CircularProgress color="primary" />
        </div>
      )}
      {clienti && (
        <>
          <h3 className="mt-2">Statistiche Clienti</h3>
          <div className="row mt-4 align-items-center stats-filter-row">
            <div className="col-md-3 stats-filter-col">
              <StatsMonthRangeFilter
                filters={filters}
                onFiltersChange={onFiltersChange}
              />
            </div>
            <div className="col-md-2 stats-filter-col">
              <select
                name="tipo_cliente"
                value={filters.tipo_cliente}
                className="form-control form-control-sm"
                onChange={onFilterChange}
              >
                <option value="">Tipo cliente</option>
                {ordersDomain &&
                  ordersDomain.tipi_cliente.map((tipo) => (
                    <option key={tipo} value={tipo}>
                      {tipo}
                    </option>
                  ))}
              </select>
            </div>
            <div className="col-md-2 stats-filter-col">
              <select
                name="raggruppa_clienti_budget"
                value={filters.raggruppa_clienti_budget}
                className="form-control form-control-sm"
                onChange={onFilterChange}
              >
                <option value="">Filiali separate</option>
                <option value="1">Gruppi budget</option>
              </select>
            </div>
            <div className="col-md-3 stats-filter-col">
              <Select
                name="agenti"
                isMulti
                placeholder={'Filtra per agenti'}
                options={agentiOptions}
                classNamePrefix="multi-select"
                hideSelectedOptions={false}
                isOptionSelected={(option) =>
                  filters.agenti && filters.agenti.includes(option.value)
                }
                value={selectedAgenti}
                className="stats-multi-select select-multi-options text-nowrap w-100"
                onChange={(newValues) => {
                  onFiltersChange({
                    ...filters,
                    agenti: newValues ? newValues.map((v) => v.value) : [],
                  })
                }}
              />
            </div>
            <div className="col-md-2 stats-filter-col">
              {/* button for reset */}
              <Button
                onClick={() =>
                  onFiltersChange({
                    tipo_cliente: '',
                    agenti: [],
                    raggruppa_clienti_budget: '1',
                    data_inizio: moment()
                      .subtract(1, 'month')
                      .startOf('month')
                      .format('YYYY-MM-DD'),
                    data_fine: moment()
                      .subtract(1, 'month')
                      .endOf('month')
                      .format('YYYY-MM-DD'),
                  })
                }
                size="sm"
                outline
                color="secondary"
              >
                Reset
              </Button>
            </div>
          </div>
          {/*  switch agenti / clienti */}
          <div className="row mt-2">
            <div className="col-md-12">
              <div className="border-bottom d-flex align-items-center w-100">
                <div
                  className="font-weight-bold border-bottom border-primary pointer"
                  onClick={() =>
                    history.push(
                      '/statistiche-clienti?data_inizio=' +
                        filters.data_inizio +
                        '&data_fine=' +
                        filters.data_fine +
                        agentiForQs
                    )
                  }
                >
                  Clienti
                </div>
                <div
                  className="ml-4 pointer"
                  onClick={() =>
                    history.push(
                      '/statistiche-agenti?data_inizio=' +
                        filters.data_inizio +
                        '&data_fine=' +
                        filters.data_fine +
                        agentiForQs
                    )
                  }
                >
                  Agenti
                </div>
              </div>
            </div>
          </div>
          <div className="row mt-2">
            <div className="col-md-12">
              <div className="stats-actions-bar d-flex align-items-center justify-content-between">
                <StatsTypeToggle value={typeStat} onChange={setTypeStat} />
                <Button
                  color="success"
                  size="sm"
                  disabled={!clienti || loading}
                  onClick={handleExportExcel}
                >
                  <i className="fas fa-file-excel mr-2"></i>
                  Esporta Excel
                </Button>
              </div>
            </div>
          </div>
          {typeStat === 'table' && (
            <div className="table-responsive stats-table-responsive">
              <table className="table table-bordered mt-2 stats-scroll-table">
                <thead>
                  <tr>
                    <td>
                      <input
                        name="search"
                        type="text"
                        className="form-control form-control-sm"
                        placeholder={'Ricerca libera'}
                        value={filters && filters.search}
                        onChange={onFilterChangeDebounced}
                      />
                    </td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <TotalBadgeCell
                      className="text-center"
                      value={totals?.totale_ordini}
                    />
                    <TotalBadgeCell
                      className="text-center"
                      value={totals?.totale_serramenti}
                    />
                    <TotalBadgeCell
                      nowrap
                      value={totals?.totale_listino}
                      format={formatStatsEuro}
                    />
                    <TotalBadgeCell
                      value={totals?.totale_sconto}
                      format={(value) => `${value.toFixed(2)} %`}
                    />
                    <TotalBadgeCell
                      nowrap
                      value={totals?.totale_media_ordini}
                      format={formatStatsEuro}
                    />
                    <TotalBadgeCell
                      nowrap
                      value={totals?.totale_media_serramenti}
                      format={formatStatsEuro}
                    />
                    <TotalBadgeCell
                      nowrap
                      value={totals?.totale_netto}
                      format={formatStatsEuro}
                    />
                    <TotalBadgeCell
                      nowrap
                      value={totals?.totale_budget}
                      format={formatStatsEuro}
                    />
                    <TotalBadgeCell
                      value={totals?.totale_differenza_netto_budget}
                      format={formatStatsPercentGap}
                    />
                    <TotalBadgeCell
                      nowrap
                      value={totals?.totale_differenza_netto_budget_euro}
                      format={formatStatsEuro}
                    />
                  </tr>
                  <tr>
                    <Column {...columnProps} field="nome">
                      Nome
                    </Column>
                    <Column {...columnProps} field="codice">
                      Codice
                    </Column>
                    <Column {...columnProps} field="tipo_cliente">
                      Tipo
                    </Column>
                    <Column {...columnProps} field="agente__user__first_name">
                      Agente
                    </Column>
                    <Column {...columnProps} field="numero_ordini">
                      N° comm.
                    </Column>
                    <Column {...columnProps} field="numero_serramenti">
                      N° serr.
                    </Column>
                    <Column {...columnProps} field="listino_serramenti">
                      Listino
                    </Column>
                    <Column {...columnProps} field="sconto_serramenti">
                      Sconto
                    </Column>
                    <Column {...columnProps} field="media_ordini">
                      Media comm.
                    </Column>
                    <Column {...columnProps} field="media_serramenti">
                      Media serr.
                    </Column>
                    <Column {...columnProps} field="netto_serramenti">
                      Netto
                    </Column>
                    <Column {...columnProps} field="budget_cliente">
                      Budget
                    </Column>
                    <Column {...columnProps} field="differenza_netto_budget">
                      Diff. %
                    </Column>
                    <Column
                      {...columnProps}
                      field="differenza_netto_budget_euro"
                    >
                      Diff. budget
                    </Column>
                  </tr>
                </thead>
                <tbody className="stats-table">
                  {clienti.map((cliente) => (
                    <tr key={cliente.id}>
                      <HighlightCell active={cliente.differenza_netto_budget}>
                        {cliente.nome}
                      </HighlightCell>
                      <HighlightCell active={cliente.differenza_netto_budget}>
                        {cliente.codice}
                      </HighlightCell>
                      <HighlightCell active={cliente.differenza_netto_budget}>
                        {cliente.tipo_cliente}
                      </HighlightCell>
                      <HighlightCell active={cliente.differenza_netto_budget}>
                        {cliente.agenti.map((agente, i) =>
                          agente === null ? (
                            ''
                          ) : (
                            <div key={i}>
                              {agenti?.find((a) => a.codice === agente)?.user
                                .first_name +
                                ' ' +
                                agenti?.find((a) => a.codice === agente)?.user
                                  .last_name}
                            </div>
                          )
                        )}
                      </HighlightCell>
                      <HighlightCell active={cliente.differenza_netto_budget}>
                        {cliente.numero_ordini}
                      </HighlightCell>
                      <HighlightCell active={cliente.differenza_netto_budget}>
                        {cliente.numero_serramenti}
                      </HighlightCell>
                      <EuroCell
                        active={cliente.differenza_netto_budget}
                        value={cliente.listino_serramenti}
                      />
                      <HighlightCell active={cliente.differenza_netto_budget}>
                        {cliente.sconto_serramenti} %
                      </HighlightCell>
                      <EuroCell
                        active={cliente.differenza_netto_budget}
                        value={cliente.media_ordini}
                      />
                      <EuroCell
                        active={cliente.differenza_netto_budget}
                        value={cliente.media_serramenti}
                      />
                      <EuroCell
                        active={cliente.differenza_netto_budget}
                        value={cliente.netto_serramenti}
                      />
                      <EuroCell
                        active={cliente.differenza_netto_budget}
                        value={cliente.budget_cliente}
                      />
                      <DifferenceCell
                        active={cliente.differenza_netto_budget}
                        value={cliente.differenza_netto_budget}
                      />
                      <EuroCell
                        active={cliente.differenza_netto_budget}
                        value={cliente.differenza_netto_budget_euro}
                      />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {typeStat === 'chart' && (
            <ResponsiveContainer width="100%" height={900} className={'mt-4'}>
              <BarChart
                layout="vertical"
                data={chartClienti}
                margin={{ top: 5, right: 30, left: 80, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  type="number"
                  dataKey="differenza_netto_budget_gap"
                  tickFormatter={formatStatsPercentValue}
                />
                <YAxis
                  type="category"
                  dataKey="nome"
                  tickFormatter={(value) => value.substring(0, 18)}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip
                  formatter={(value) => formatStatsPercentValue(value)}
                />
                {/* <Legend /> */}
                <Bar dataKey="differenza_netto_budget_gap">
                  {chartClienti.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={getDifferenceColor(entry.differenza_netto_budget)}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </>
      )}
    </div>
  )
}
